import type * as React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { getSportColor, type DifficultyLevel } from "../../workoutStatus";
import { intensityChip } from "./intensity";

/**
 * WeekView — 7-day grid of planned sessions for the athlete Plan.
 *
 * Pure presentational atom. Caller hands:
 *   - dayCards: 7-element array, one per weekday (Mon..Sun)
 *   - todayWeekday: 0..6 so we can highlight the current column
 *   - onSessionClick: optional handler for the drawer
 *
 * Each day card lists 0..N sessions (rest days = empty). Race-day
 * cards get a separate visual treatment (green border + race badge)
 * driven by the dayCard.isRace flag.
 */

export interface PlanSessionCard {
  id: number | string;
  time?: string | null;        // "HH:MM" or null
  sport: string;               // run/cycle/swim/...
  title: string;
  volumeLabel?: string | null; // "12 км" or "60 мин"
  tss?: number | null;
  /**
   * Уровень тяжести 1..4 — уже посчитанный, не выводимый из tss.
   *
   * Считается при сборке карточки, где есть хроническая нагрузка атлета:
   * тащить CTL пропсом через WeekView и ListView в каждую ячейку значило бы
   * раздавать контекст ради арифметики, которую сборка делает один раз.
   */
  difficultyLevel?: DifficultyLevel | null;
  zone?: string | null;        // "Z2", "Z4-Z5"
  hrTarget?: string | null;    // "138-148"
  status?: "planned" | "done" | "skipped" | "swapped" | "race";
  isRace?: boolean;
}

export interface PlanDayCard {
  weekday: number;             // 0..6, Mon=0
  weekdayLabel: string;        // "пн", "вт", etc.
  date: number;                // day of month
  /** ISO yyyy-mm-dd — required when onSessionMove is wired. */
  dateISO?: string;
  sessions: PlanSessionCard[];
  isToday?: boolean;
  isRace?: boolean;
}

export interface WeekViewProps {
  dayCards: PlanDayCard[];
  onSessionClick?: (session: PlanSessionCard) => void;
  /**
   * Optional drag-and-drop reorder. When set, sessions become
   * draggable (HTML5 native API) and day cards become drop targets.
   * Callback receives the session id and the destination day ISO.
   */
  onSessionMove?: (
    sessionId: number | string,
    fromDateISO: string,
    toDateISO: string,
  ) => void;
  className?: string;
}

// 2026-07-28: своя таблица дисциплин удалена — это была третья копия одной
// и той же карты, и, как все копии, она отстала: trail красился лаймом
// --active-500 (снятым с этой роли), gravel цветом БЕГА, triathlon цветом
// ВЕЛО, а duathlon/hyrox/strength — сигнальными warning/danger/muted вместо
// собственных цветов. Единый источник — SPORT_COLOR в workoutStatus.ts;
// getSportColor заодно нормализует написание дисциплины (getSportKey).
const sportDot = getSportColor;

export function WeekView({ dayCards, onSessionClick, onSessionMove, className = "" }: WeekViewProps) {
  const { t } = useTranslation("plan");
  const dndEnabled = !!onSessionMove;

  const onDayDragOver = (ev: React.DragEvent<HTMLDivElement>) => {
    if (!dndEnabled) return;
    if (ev.dataTransfer.types.includes("application/x-plan-session")) {
      ev.preventDefault();
      ev.dataTransfer.dropEffect = "move";
    }
  };

  const onDayDrop = (toDateISO: string | undefined) => (ev: React.DragEvent<HTMLDivElement>) => {
    if (!dndEnabled || !toDateISO) return;
    const payload = ev.dataTransfer.getData("application/x-plan-session");
    if (!payload) return;
    ev.preventDefault();
    try {
      const { id, fromDateISO } = JSON.parse(payload) as { id: number | string; fromDateISO: string };
      if (id == null || !fromDateISO || fromDateISO === toDateISO) return;
      onSessionMove!(id, fromDateISO, toDateISO);
    } catch {
      // silent: malformed dataTransfer payload — ignore drop.
    }
  };

  const onSessionDragStart = (
    sessionId: number | string,
    fromDateISO: string | undefined,
  ) => (ev: React.DragEvent<HTMLElement>) => {
    if (!dndEnabled || !fromDateISO) return;
    ev.dataTransfer.setData(
      "application/x-plan-session",
      JSON.stringify({ id: sessionId, fromDateISO }),
    );
    ev.dataTransfer.effectAllowed = "move";
  };

  return (
    <div
      data-testid="week-view"
      className={`grid grid-cols-2 md:grid-cols-7 gap-2 ${className}`}
    >
      {dayCards.map((day) => {
        const dayTone =
          day.isRace
            ? "border-ok/40 bg-ok/[0.06]"
            : day.isToday
            ? "border-accent/40 bg-accent/[0.06]"
            : "border-bg-border bg-bg-surface";

        return (
          <div
            key={day.weekday}
            data-testid={`week-view-day-${day.weekday}`}
            data-is-today={day.isToday ? "true" : "false"}
            data-is-race={day.isRace ? "true" : "false"}
            onDragOver={onDayDragOver}
            onDrop={onDayDrop(day.dateISO)}
            className={`rounded-card border p-2.5 flex flex-col gap-1.5 ${
              day.sessions.length === 0 ? "" : "min-h-[64px]"
            } ${dayTone}`}
          >
            <div className="flex items-baseline justify-between">
              <span
                className={`text-[9.5px] uppercase tracking-[0.18em] font-bold ${
                  day.isRace ? "text-ok" : day.isToday ? "text-accent" : "text-fg-muted"
                }`}
              >
                {day.weekdayLabel}
              </span>
              <span className="text-sm font-bold text-fg-primary tabular-nums">
                {day.date}
              </span>
            </div>

            {day.sessions.length === 0 ? (
              <div
                data-testid={`week-view-rest-${day.weekday}`}
                className="text-[11px] text-fg-muted italic"
              >
                {t("restDay", { defaultValue: "День отдыха" })}
              </div>
            ) : (
              <div className="flex flex-col gap-1.5">
                {day.sessions.map((s) => (
                  <button
                    type="button"
                    key={s.id}
                    data-testid={`week-view-session-${s.id}`}
                    data-sport={s.sport}
                    data-draggable={dndEnabled ? "true" : "false"}
                    draggable={dndEnabled}
                    onDragStart={onSessionDragStart(s.id, day.dateISO)}
                    onClick={onSessionClick ? () => onSessionClick(s) : undefined}
                    disabled={!onSessionClick}
                    aria-label={
                      dndEnabled
                        ? t("week_view.drag_aria", {
                            defaultValue: "Перетащи на другой день, чтобы перенести",
                          })
                        : undefined
                    }
                    className={`text-left rounded-md px-2 py-1.5 bg-bg-raised/40 hover:bg-bg-raised active:bg-bg-raised transition ${
                      onSessionClick ? "cursor-pointer" : "cursor-default"
                    } ${dndEnabled ? "cursor-grab active:cursor-grabbing" : ""}`}
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className="w-1.5 h-1.5 rounded-full inline-block flex-shrink-0"
                        style={{ backgroundColor: sportDot(s.sport) }}
                      />
                      {s.time && (
                        <span className="text-[9.5px] tabular-nums text-fg-muted font-semibold">
                          {s.time}
                        </span>
                      )}
                      {s.isRace && (
                        <span className="text-[8.5px] uppercase tracking-[0.18em] text-ok font-bold">
                          {t("phase_strip.race", { defaultValue: "ГОНКА" })}
                        </span>
                      )}
                    </div>
                    <div className="mt-1 text-[11.5px] font-semibold text-fg-primary leading-tight">
                      {s.title}
                    </div>
                    <div className="mt-0.5 flex gap-1.5 flex-wrap items-center">
                      {s.volumeLabel && (
                        <span className="text-[9px] uppercase tracking-[0.14em] text-fg-secondary font-semibold">
                          {s.volumeLabel}
                        </span>
                      )}
                      {s.tss != null && (
                        <span className="text-[9px] uppercase tracking-[0.14em] text-fg-muted font-semibold tabular-nums">
                          {Math.round(s.tss)} TSS
                        </span>
                      )}
                      {s.zone && (() => {
                        // Phase E: zone chip → /zones?z=Z4 deep-link. Range
                        // labels like "Z4-Z5" link to the first zone (Z4).
                        // stopPropagation: zone chip shouldn't open the
                        // session modal, just navigate.
                        const m = s.zone.match(/Z([1-7])/i);
                        if (!m) {
                          return (
                            <span className="text-[9px] uppercase tracking-[0.14em] text-fg-muted font-semibold">
                              {s.zone}
                            </span>
                          );
                        }
                        const zKey = `Z${m[1]}`;
                        return (
                          <Link
                            to={`/zones?z=${zKey}`}
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`week-view-zone-chip-${s.id}`}
                            aria-label={`${s.zone} · ${t("common:aria.go_to_zone", { defaultValue: "перейти к зоне" })}`}
                            className="text-[9px] uppercase tracking-[0.14em] text-fg-muted hover:text-fg-primary font-semibold underline decoration-dotted underline-offset-2"
                          >
                            {s.zone}
                          </Link>
                        );
                      })()}
                      {(() => {
                        const intensity = intensityChip(s.difficultyLevel);
                        if (!intensity) return null;
                        return (
                          <span
                            data-testid={`week-view-intensity-${s.id}`}
                            data-intensity={intensity.level}
                            className={intensity.className}
                          >
                            {t(`intensity.${intensity.level.toLowerCase()}`, { defaultValue: intensity.level })}
                          </span>
                        );
                      })()}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
